import React, { useMemo, useState } from 'react';
import { Dialog, DialogTitle, DialogContent, DialogActions, Button, Box, Typography, RadioGroup, FormControlLabel, Radio, Checkbox, Stack, Tooltip, Chip, Divider, Alert } from '@mui/material';
import { API_BASE_URL } from '../../config';
import axiosInstance from '../../services/axiosInstance';

interface Props {
  open: boolean;
  assetA: any; // primary by default
  assetB: any; // secondary
  onClose: () => void;
  onMerged: () => void;
}

const DuplicateMergeDialog: React.FC<Props> = ({ open, assetA, assetB, onClose, onMerged }) => {
  const base = API_BASE_URL || (process.env.REACT_APP_API_BASE_URL as string) || '';
  const srcA = assetA?.previewPaths?.thumbnail ? `${base}${assetA.previewPaths.thumbnail}` : `${base}/${(assetA?.filePath || '').replace(/\\/g,'/')}`;
  const srcB = assetB?.previewPaths?.thumbnail ? `${base}${assetB.previewPaths.thumbnail}` : `${base}/${(assetB?.filePath || '').replace(/\\/g,'/')}`;

  // Which side to keep per field
  const [titleFrom, setTitleFrom] = useState<'A'|'B'>('A');
  const [descFrom, setDescFrom] = useState<'A'|'B'>('A');
  const [tagsFrom, setTagsFrom] = useState<'A'|'B'>('A');
  const [aiTagsFrom, setAiTagsFrom] = useState<'A'|'B'>('A');
  const [aiDescFrom, setAiDescFrom] = useState<'A'|'B'>('A');
  const [catsUnion, setCatsUnion] = useState(true);
  const [tagsUnion, setTagsUnion] = useState(true);
  const [aiTagsUnion, setAiTagsUnion] = useState(true);
  const [note, setNote] = useState<string>('Merged via duplicate tool');

  const chosenTitle = titleFrom === 'A' ? (assetA?.title || '') : (assetB?.title || '');
  const chosenDesc  = descFrom  === 'A' ? (assetA?.description || '') : (assetB?.description || '');
  const chosenTags  = useMemo(() => {
    const a = Array.isArray(assetA?.tags) ? assetA.tags : [];
    const b = Array.isArray(assetB?.tags) ? assetB.tags : [];
    if (tagsUnion) return Array.from(new Set([...(a||[]), ...(b||[])]));
    return tagsFrom === 'A' ? a : b;
  }, [assetA, assetB, tagsFrom, tagsUnion]);
  const chosenAiTags  = useMemo(() => {
    const a = Array.isArray(assetA?.aiTags) ? assetA.aiTags : [];
    const b = Array.isArray(assetB?.aiTags) ? assetB.aiTags : [];
    if (aiTagsUnion) return Array.from(new Set([...(a||[]), ...(b||[])]));
    return aiTagsFrom === 'A' ? a : b;
  }, [assetA, assetB, aiTagsFrom, aiTagsUnion]);
  const chosenAiDesc = aiDescFrom === 'A' ? (assetA?.aiDescription || '') : (assetB?.aiDescription || '');
  const chosenCats   = useMemo(() => {
    const a = Array.isArray(assetA?.categories) ? assetA.categories : [];
    const b = Array.isArray(assetB?.categories) ? assetB.categories : [];
    if (catsUnion) return Array.from(new Set([...(a||[]).map(String), ...(b||[]).map(String)]));
    // Behåll kategorier från A som standard
    return a;
  }, [assetA, assetB, catsUnion]);

  const handleConfirm = async () => {
    try {
      // 1) Lägg B som ny version på A
      await axiosInstance.post('/api/duplicates/merge-version', { primaryId: assetA._id, secondaryId: assetB._id, note });
      // 2) Uppdatera metadata på A enligt val
      await axiosInstance.put(`/api/assets/${assetA._id}`, {
        title: chosenTitle,
        description: chosenDesc,
        tags: chosenTags,
        aiTags: chosenAiTags,
        aiDescription: chosenAiDesc,
        categories: chosenCats
      });
      onMerged();
      onClose();
    } catch (e: any) {
      alert(e?.response?.data?.message || e?.message || 'Merge misslyckades.');
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      onClick={(event: React.MouseEvent) => event.stopPropagation()}
      onMouseDown={(event: React.MouseEvent) => event.stopPropagation()}
      onMouseUp={(event: React.MouseEvent) => event.stopPropagation()}
      slotProps={{
        backdrop: {
          onClick: (event: React.MouseEvent) => event.stopPropagation(),
          onMouseDown: (event: React.MouseEvent) => event.stopPropagation(),
          onMouseUp: (event: React.MouseEvent) => event.stopPropagation()
        }
      }}
      PaperProps={{
        onClick: (event: React.MouseEvent) => event.stopPropagation(),
        onMouseDown: (event: React.MouseEvent) => event.stopPropagation(),
        onMouseUp: (event: React.MouseEvent) => event.stopPropagation()
      }}
    >
      <DialogTitle>Slå ihop med metadata‑val</DialogTitle>
      <DialogContent>
        <Alert severity="info" sx={{ mb: 3 }}>
          <Typography variant="body2">
            <strong>Vad händer vid sammanslagning:</strong>
          </Typography>
          <Typography variant="body2" component="ul" sx={{ pl: 2, mt: 0.5, mb: 0 }}>
            <li>Bild B läggs som en version på bild A (historik bevaras)</li>
            <li>Metadata uppdateras på A enligt dina val nedan</li>
            <li><strong>Bild B arkiveras</strong> och försvinner från huvudvyn</li>
            <li>B kan återställas från arkivet vid behov</li>
          </Typography>
        </Alert>
        
        {/* Image comparison with better styling */}
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} sx={{ mb: 3 }}>
          <Box 
            sx={{ 
              flex: 1, 
              p: 2, 
              border: '2px solid #1976d2', 
              borderRadius: '8px',
              bgcolor: 'rgba(25, 118, 210, 0.05)'
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
              <Chip 
                label="A" 
                size="small" 
                sx={{ 
                  bgcolor: '#1976d2', 
                  color: 'white',
                  fontWeight: 'bold'
                }} 
              />
              <Typography variant="subtitle2" fontWeight="bold">Primär (behålls)</Typography>
            </Box>
            <Box 
              component="img" 
              src={srcA} 
              alt="A" 
              sx={{ 
                width: '100%', 
                maxHeight: 200, 
                objectFit: 'contain', 
                borderRadius: 1, 
                mb: 1,
                bgcolor: '#f5f5f5',
                p: 1
              }} 
            />
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', fontWeight: 'bold' }}>
              {assetA?.fileName}
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ display:'block' }}>
              Taggar: {(assetA?.tags || []).join(', ') || '—'}
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ display:'block' }}>
              AI-taggar: {(assetA?.aiTags || []).join(', ') || '—'}
            </Typography>
          </Box>
          <Box 
            sx={{ 
              flex: 1, 
              p: 2, 
              border: '2px solid #d32f2f', 
              borderRadius: '8px',
              bgcolor: 'rgba(211, 47, 47, 0.05)'
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
              <Chip 
                label="B" 
                size="small" 
                sx={{ 
                  bgcolor: '#d32f2f', 
                  color: 'white',
                  fontWeight: 'bold'
                }} 
              />
              <Typography variant="subtitle2" fontWeight="bold">Sekundär (blir version)</Typography>
            </Box>
            <Box 
              component="img" 
              src={srcB} 
              alt="B" 
              sx={{ 
                width: '100%', 
                maxHeight: 200, 
                objectFit: 'contain', 
                borderRadius: 1, 
                mb: 1,
                bgcolor: '#f5f5f5',
                p: 1
              }} 
            />
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', fontWeight: 'bold' }}>
              {assetB?.fileName}
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ display:'block' }}>
              Taggar: {(assetB?.tags || []).join(', ') || '—'}
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ display:'block' }}>
              AI-taggar: {(assetB?.aiTags || []).join(', ') || '—'}
            </Typography>
          </Box>
        </Stack>

        <Divider sx={{ my: 2 }} />
        
        <Typography variant="h6" sx={{ mb: 2 }}>Välj metadata att behålla</Typography>
        
        {/* Two column layout: Selections on left, Result preview on right */}
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>
          {/* Left column - Metadata selections */}
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Stack spacing={2}>
              {/* Textfält - Kompakt */}
              <Box 
                sx={{ 
                  p: 1.5, 
                  bgcolor: '#fafafa', 
                  borderRadius: '8px',
                  border: '1px solid #e0e0e0'
                }}
              >
                <Typography variant="subtitle2" fontWeight="bold" sx={{ mb: 1.5 }}>Textinformation</Typography>
                
                <Box sx={{ mb: 1.5 }}>
                  <Typography variant="caption" fontWeight="bold" sx={{ mb: 0.5, display: 'block' }}>Titel</Typography>
                  <RadioGroup row value={titleFrom} onChange={(_,v)=>setTitleFrom(v as any)}>
                    <FormControlLabel 
                      value="A" 
                      control={<Radio size="small" sx={{ color: '#1976d2', '&.Mui-checked': { color: '#1976d2' } }} />} 
                      label={<Chip label="A" size="small" sx={{ bgcolor: '#1976d2', color: 'white', height: 18 }} />}
                    />
                    <FormControlLabel 
                      value="B" 
                      control={<Radio size="small" sx={{ color: '#d32f2f', '&.Mui-checked': { color: '#d32f2f' } }} />} 
                      label={<Chip label="B" size="small" sx={{ bgcolor: '#d32f2f', color: 'white', height: 18 }} />}
                    />
                  </RadioGroup>
                </Box>

                <Divider sx={{ my: 1, borderColor: '#e0e0e0' }} />

                <Box sx={{ mb: 1.5 }}>
                  <Typography variant="caption" fontWeight="bold" sx={{ mb: 0.5, display: 'block' }}>Beskrivning</Typography>
                  <RadioGroup row value={descFrom} onChange={(_,v)=>setDescFrom(v as any)}>
                    <FormControlLabel 
                      value="A" 
                      control={<Radio size="small" sx={{ color: '#1976d2', '&.Mui-checked': { color: '#1976d2' } }} />} 
                      label={<Chip label="A" size="small" sx={{ bgcolor: '#1976d2', color: 'white', height: 18 }} />}
                    />
                    <FormControlLabel 
                      value="B" 
                      control={<Radio size="small" sx={{ color: '#d32f2f', '&.Mui-checked': { color: '#d32f2f' } }} />} 
                      label={<Chip label="B" size="small" sx={{ bgcolor: '#d32f2f', color: 'white', height: 18 }} />}
                    />
                  </RadioGroup>
                </Box>

                <Divider sx={{ my: 1, borderColor: '#e0e0e0' }} />

                <Box>
                  <Typography variant="caption" fontWeight="bold" sx={{ mb: 0.5, display: 'block' }}>AI-beskrivning</Typography>
                  <RadioGroup row value={aiDescFrom} onChange={(_,v)=>setAiDescFrom(v as any)}>
                    <FormControlLabel 
                      value="A" 
                      control={<Radio size="small" sx={{ color: '#1976d2', '&.Mui-checked': { color: '#1976d2' } }} />} 
                      label={<Chip label="A" size="small" sx={{ bgcolor: '#1976d2', color: 'white', height: 18 }} />}
                    />
                    <FormControlLabel 
                      value="B" 
                      control={<Radio size="small" sx={{ color: '#d32f2f', '&.Mui-checked': { color: '#d32f2f' } }} />} 
                      label={<Chip label="B" size="small" sx={{ bgcolor: '#d32f2f', color: 'white', height: 18 }} />}
                    />
                  </RadioGroup>
                </Box>
              </Box>

              {/* Taggar och kategorier - Kompakt */}
              <Box 
                sx={{ 
                  p: 1.5, 
                  bgcolor: '#fafafa', 
                  borderRadius: '8px',
                  border: '1px solid #e0e0e0'
                }}
              >
                <Typography variant="subtitle2" fontWeight="bold" sx={{ mb: 1.5 }}>Taggar och kategorier</Typography>
                
                <Box sx={{ mb: 1.5 }}>
                  <Typography variant="caption" fontWeight="bold" sx={{ mb: 0.5, display: 'block' }}>Taggar</Typography>
                  <RadioGroup row value={tagsFrom} onChange={(_,v)=>setTagsFrom(v as any)} sx={{ mb: 0.5 }}>
                    <FormControlLabel 
                      value="A" 
                      control={<Radio size="small" sx={{ color: '#1976d2', '&.Mui-checked': { color: '#1976d2' } }} />} 
                      label={<Chip label="A" size="small" sx={{ bgcolor: '#1976d2', color: 'white', height: 18 }} />}
                    />
                    <FormControlLabel 
                      value="B" 
                      control={<Radio size="small" sx={{ color: '#d32f2f', '&.Mui-checked': { color: '#d32f2f' } }} />} 
                      label={<Chip label="B" size="small" sx={{ bgcolor: '#d32f2f', color: 'white', height: 18 }} />}
                    />
                  </RadioGroup>
                  <FormControlLabel 
                    control={<Checkbox size="small" checked={tagsUnion} onChange={e=>setTagsUnion(e.target.checked)} />} 
                    label={<Typography variant="caption">Slå ihop från både A och B</Typography>}
                  />
                </Box>

                <Divider sx={{ my: 1, borderColor: '#e0e0e0' }} />

                <Box sx={{ mb: 1.5 }}>
                  <Typography variant="caption" fontWeight="bold" sx={{ mb: 0.5, display: 'block' }}>AI-taggar</Typography>
                  <RadioGroup row value={aiTagsFrom} onChange={(_,v)=>setAiTagsFrom(v as any)} sx={{ mb: 0.5 }}>
                    <FormControlLabel 
                      value="A" 
                      control={<Radio size="small" sx={{ color: '#1976d2', '&.Mui-checked': { color: '#1976d2' } }} />} 
                      label={<Chip label="A" size="small" sx={{ bgcolor: '#1976d2', color: 'white', height: 18 }} />}
                    />
                    <FormControlLabel 
                      value="B" 
                      control={<Radio size="small" sx={{ color: '#d32f2f', '&.Mui-checked': { color: '#d32f2f' } }} />} 
                      label={<Chip label="B" size="small" sx={{ bgcolor: '#d32f2f', color: 'white', height: 18 }} />}
                    />
                  </RadioGroup>
                  <FormControlLabel 
                    control={<Checkbox size="small" checked={aiTagsUnion} onChange={e=>setAiTagsUnion(e.target.checked)} />} 
                    label={<Typography variant="caption">Slå ihop från både A och B</Typography>}
                  />
                </Box>

                <Divider sx={{ my: 1, borderColor: '#e0e0e0' }} />

                <Box>
                  <Typography variant="caption" fontWeight="bold" sx={{ mb: 0.5, display: 'block' }}>Kategorier</Typography>
                  <FormControlLabel 
                    control={<Checkbox size="small" checked={catsUnion} onChange={e=>setCatsUnion(e.target.checked)} />} 
                    label={<Typography variant="caption">Slå ihop från både A och B</Typography>}
                  />
                </Box>
              </Box>
            </Stack>
          </Box>

          {/* Right column - Result preview (sticky, scrollable) */}
          <Box 
            sx={{ 
              flex: 1,
              position: 'sticky',
              top: 0,
              p: 1.5, 
              bgcolor: '#e8f5e9',
              border: '2px solid #4caf50', 
              borderRadius: '8px',
              minWidth: 0,
              maxHeight: '70vh',
              overflow: 'auto'
            }}
          >
            <Typography variant="subtitle2" fontWeight="bold" sx={{ mb: 1.5, color: '#2e7d32' }}>
              ✓ Resultat (allt som kommer sparas)
            </Typography>
            
            <Box sx={{ bgcolor: 'white', p: 1.5, borderRadius: 1 }}>
              <Typography variant="caption" sx={{ mb: 0.5, display: 'block', wordBreak: 'break-word' }}>
                <strong>Titel:</strong> {chosenTitle || '—'}
              </Typography>
              
              <Typography variant="caption" sx={{ mb: 0.5, display: 'block', wordBreak: 'break-word' }}>
                <strong>Beskrivning:</strong> {chosenDesc || '—'}
              </Typography>
              
              <Typography variant="caption" sx={{ mb: 1, display: 'block', wordBreak: 'break-word' }}>
                <strong>AI-beskrivning:</strong> {chosenAiDesc || '—'}
              </Typography>
              
              <Box sx={{ mb: 1 }}>
                <Typography variant="caption" fontWeight="bold" sx={{ display: 'block', mb: 0.5 }}>
                  Taggar ({chosenTags?.length || 0} st):
                </Typography>
                {chosenTags && chosenTags.length > 0 ? (
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                    {chosenTags.map((t: string, idx: number) => (
                      <Chip size="small" key={`${t}-${idx}`} label={t} sx={{ height: 18, fontSize: '0.7rem' }} />
                    ))}
                  </Box>
                ) : (
                  <Typography variant="caption" color="text.secondary">Inga taggar</Typography>
                )}
              </Box>
              
              <Box sx={{ mb: 1 }}>
                <Typography variant="caption" fontWeight="bold" sx={{ display: 'block', mb: 0.5 }}>
                  AI-taggar ({chosenAiTags?.length || 0} st):
                </Typography>
                {chosenAiTags && chosenAiTags.length > 0 ? (
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                    {chosenAiTags.map((t: string, idx: number) => (
                      <Chip size="small" key={`${t}-${idx}`} label={t} color="primary" variant="outlined" sx={{ height: 18, fontSize: '0.7rem' }} />
                    ))}
                  </Box>
                ) : (
                  <Typography variant="caption" color="text.secondary">Inga AI-taggar</Typography>
                )}
              </Box>
              
              <Box>
                <Typography variant="caption" fontWeight="bold" sx={{ display: 'block', mb: 0.5 }}>
                  Kategorier ({Array.isArray(chosenCats) ? chosenCats.length : 0} st):
                </Typography>
                {chosenCats && chosenCats.length > 0 ? (
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                    {chosenCats.map((catId: string, idx: number) => {
                      const category = [...(assetA?.categories || []), ...(assetB?.categories || [])]
                        .find((c: any) => String(c._id || c) === String(catId));
                      const catName = category?.name || catId;
                      return (
                        <Chip 
                          size="small" 
                          key={`${catId}-${idx}`} 
                          label={catName} 
                          color="secondary"
                          variant="outlined"
                          sx={{ height: 18, fontSize: '0.7rem' }} 
                        />
                      );
                    })}
                  </Box>
                ) : (
                  <Typography variant="caption" color="text.secondary">Inga kategorier</Typography>
                )}
              </Box>
            </Box>
          </Box>
        </Box>
      </DialogContent>
      <DialogActions sx={{ p: 2, bgcolor: '#f5f5f5' }}>
        <Typography variant="caption" color="text.secondary" sx={{ flex: 1, ml: 1 }}>
          Bild B kommer att arkiveras efter sammanslagning
        </Typography>
        <Button onClick={onClose}>Avbryt</Button>
        <Tooltip title="Lägger B som version på A, uppdaterar metadata och arkiverar B">
          <Button variant="contained" color="primary" onClick={handleConfirm}>
            Slå ihop och arkivera B
          </Button>
        </Tooltip>
      </DialogActions>
    </Dialog>
  );
};

export default DuplicateMergeDialog;


